import React from 'react';
import { Box, Button, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import CircleIcon from '@mui/icons-material/Circle';
import CheckIcon from '@mui/icons-material/Check';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import MarkEmailUnreadOutlinedIcon from '@mui/icons-material/MarkEmailUnreadOutlined';
import PersonOutlineRoundedIcon from '@mui/icons-material/PersonOutlineRounded';
import { COLORS } from '../../theme';
import { PRIORITY_COLORS, formatRelativeTime, getCategoryVisual } from './notificationVisuals';

/**
 * Item individual do drawer de notificações.
 */
const NotificationItem = ({ notificacao, onOpen, onMarkRead, onMarkUnread, onRemove }) => {
  const visual = getCategoryVisual(notificacao.categoria);
  const Icon = visual.Icon;

  const isUnread = !notificacao.lida;
  const clienteNome = notificacao.dados?.clienteNome || null;
  // Em "visualizou/seleção" o nome já aparece na mensagem; o chip só entra
  // quando o texto não cita o cliente (vídeo, pagamento, entrega etc.).
  const mostrarCliente =
    Boolean(clienteNome) && !String(notificacao.mensagem || '').includes(clienteNome);

  return (
    <Box
      component="article"
      sx={{
        display: 'flex',
        gap: 1.5,
        p: 1.5,
        borderRadius: 2,
        bgcolor: isUnread ? 'action.hover' : 'transparent',
        border: '1px solid',
        borderColor: isUnread ? 'rgba(235, 91, 37, 0.28)' : 'divider',
        transition: 'background-color 150ms ease',
        '&:hover': { bgcolor: 'action.selected' },
      }}
    >
      <Box
        aria-hidden
        sx={{
          width: 38,
          height: 38,
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'rgba(235, 91, 37, 0.12)',
          color: COLORS.orange,
          flexShrink: 0,
        }}
      >
        <Icon fontSize="small" />
      </Box>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Stack direction="row" spacing={0.75} alignItems="center">
          <Typography
            variant="subtitle2"
            sx={{ fontWeight: isUnread ? 800 : 600, overflowWrap: 'anywhere' }}
          >
            {notificacao.titulo}
          </Typography>
          {isUnread && (
            <CircleIcon
              aria-label="Não lida"
              sx={{ fontSize: 8, color: 'error.main', flexShrink: 0 }}
            />
          )}
        </Stack>

        {/* Só as notificações de admin carregam `dados.clienteNome`. */}
        {mostrarCliente && (
          <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.5, minWidth: 0 }}>
            <PersonOutlineRoundedIcon
              aria-hidden
              sx={{ fontSize: 14, color: 'text.secondary', flexShrink: 0 }}
            />
            <Typography
              variant="caption"
              title={clienteNome}
              sx={{
                fontWeight: 700,
                color: 'text.secondary',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {clienteNome}
            </Typography>
          </Stack>
        )}

        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mt: 0.5, overflowWrap: 'anywhere' }}
        >
          {notificacao.mensagem}
        </Typography>

        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          sx={{ mt: 1, flexWrap: 'wrap', rowGap: 0.5 }}
        >
          <Typography variant="caption" color="text.disabled">
            {formatRelativeTime(notificacao.createdAt)}
          </Typography>
          <Typography
            variant="caption"
            sx={{ color: PRIORITY_COLORS[notificacao.prioridade] || 'text.secondary' }}
          >
            {visual.label}
          </Typography>
          {notificacao.acao?.url && (
            <Button
              size="small"
              onClick={() => onOpen?.(notificacao)}
              sx={{ textTransform: 'none', py: 0, minWidth: 0 }}
            >
              {notificacao.acao.label || 'Abrir'}
            </Button>
          )}
        </Stack>
      </Box>

      <Stack spacing={0.25} sx={{ flexShrink: 0 }}>
        {isUnread && (
          <Tooltip title="Marcar como lida" arrow>
            <IconButton
              size="small"
              aria-label={`Marcar "${notificacao.titulo}" como lida`}
              onClick={() => onMarkRead?.(notificacao.id)}
            >
              <CheckIcon fontSize="inherit" />
            </IconButton>
          </Tooltip>
        )}
        {!isUnread && onMarkUnread && (
          <Tooltip title="Marcar como não lida" arrow>
            <IconButton
              size="small"
              aria-label={`Marcar "${notificacao.titulo}" como não lida`}
              onClick={() => onMarkUnread(notificacao.id)}
            >
              <MarkEmailUnreadOutlinedIcon fontSize="inherit" />
            </IconButton>
          </Tooltip>
        )}
        <Tooltip title="Remover" arrow>
          <IconButton
            size="small"
            aria-label={`Remover "${notificacao.titulo}"`}
            onClick={() => onRemove?.(notificacao.id)}
          >
            <DeleteOutlineIcon fontSize="inherit" />
          </IconButton>
        </Tooltip>
      </Stack>
    </Box>
  );
};

export default NotificationItem;
